import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { HttpOptions } from '../interfaces/http-params.interface';
import { ApiError, ApiResponse } from '../interfaces/api-responce.interface';
import { environment } from '../../../environment/environment';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private apiUrl = environment.apiUrl;
  getTokenValue(): any {
    const _token = localStorage.getItem('token')?.split('"').join('');
    return _token;
  }
  constructor(private http: HttpClient) {
    // console.log('Api ', this.apiUrl);
  }

  /**
   * Utility method to process parameters for HTTP requests
   * Handles various parameter types including arrays, strings, and numbers
   * @param options The HTTP options containing parameters
   * @returns HttpParams object with properly formatted parameters
   */
  fetchToken(data:any) {
    return this.http.post('users/api/login_m/', data);
  }
  private processParams(options?: HttpOptions): HttpParams {
    if (!options?.params) return new HttpParams();

    let httpParams = new HttpParams();

    Object.entries(options.params).forEach(([key, value]) => {
      if (value === undefined || value === null) {
        return; // Skip undefined or null values
      }

      if (Array.isArray(value)) {
        // Handle array values - can be used for multi-select filters
        if (value.length === 0) return; // Skip empty arrays

        // Option 1: Append as comma-separated string
        httpParams = httpParams.set(key, value.join(','));

        // Option 2: Append multiple parameters with the same key
        // value.forEach(item => {
        //   httpParams = httpParams.append(key, String(item));
        // });
      } else if (typeof value === 'object') {
        // Handle nested objects by converting to JSON string
        httpParams = httpParams.set(key, JSON.stringify(value));
      } else {
        // Handle primitive values (string, number, boolean)
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }

  /**
   * Get request to fetch data from an endpoint.
   * @param endpoint The API endpoint (e.g., 'users/profile').
   * @param options Optional query parameters.
   * @returns Observable of the API response.
   */
  get<T>(endpoint: string, options?: HttpOptions): Observable<any> {
    return this.http
      .get<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, {
        params: this.processParams(options),
      })
      .pipe(catchError(this.handleError));
  }
  getFile<T>(endpoint: string, options?: HttpOptions): Observable<any> {
    return this.http
      .get(`${this.apiUrl}/${endpoint}`, {
        responseType: 'blob',
      })
      .pipe(catchError(this.handleError));
  }
  /**
   * Post request to send data to an endpoint.
   * @param endpoint The API endpoint (e.g., 'users/login').
   * @param data The data to be sent in the request body.
   * @param options Optional query parameters.
   * @returns Observable of the API response.
   */
  post<T>(endpoint: string, data?: any, options?: HttpOptions): Observable<any> {
    return this.http
      .post<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, data, {
        params: this.processParams(options),
      })
      .pipe(catchError(this.handleError));
  }
  /**
   * Post request to send form data to an endpoint.
   * @param endpoint The API endpoint (e.g., 'leave/create-leave-request').
   * @param formDataObject The form data to be sent.
   * @param options Optional query parameters.
   * @returns Observable of the API response.
   */
  postWithFormData<T>(
    endpoint: string,
    formDataObject: Record<string, any>,
    options?: HttpOptions
  ): Observable<any> {
    const formData = new FormData();

    for (const key in formDataObject) {
      if (formDataObject.hasOwnProperty(key)) {
        const value = formDataObject[key];

        if (value instanceof File || value instanceof Blob) {
          formData.append(
            key,
            value,
            value instanceof File ? value.name : undefined
          );
        } else if (Array.isArray(value)) {
          // Handle arrays by appending each value with the same key
          value.forEach((item) => {
            formData.append(key, item);
          });
        } else if (value instanceof Object && !(value instanceof Date)) {
          // Handle nested objects by converting to JSON string
          formData.append(key, JSON.stringify(value));
        } else {
          // Handle primitive values and Date objects
          formData.append(key, value);
        }
      }
    }

    return this.http
      .post<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, formData, {
        params: this.processParams(options),
      })
      .pipe(catchError(this.handleError));
  }
  putWithFormData<T>(
    endpoint: string,
    formDataObject: Record<string, any>,
    options?: HttpOptions
  ): Observable<any> {
    const formData = new FormData();

    for (const key in formDataObject) {
      if (formDataObject.hasOwnProperty(key)) {
        const value = formDataObject[key];

        if (value instanceof File || value instanceof Blob) {
          formData.append(
            key,
            value,
            value instanceof File ? value.name : undefined
          );
        } else if (Array.isArray(value)) {
          // Handle arrays by appending each value with the same key
          value.forEach((item) => {
            formData.append(key, item);
          });
        } else if (value instanceof Object && !(value instanceof Date)) {
          // Handle nested objects by converting to JSON string
          formData.append(key, JSON.stringify(value));
        } else {
          // Handle primitive values and Date objects
          formData.append(key, value);
        }
      }
    }

    return this.http
      .put<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, formData, {
        params: this.processParams(options),
      })
      .pipe(catchError(this.handleError));
  }
  /**
   * Put request to update data at an endpoint.
   * @param endpoint The API endpoint (e.g., 'users/123').
   * @param data The data to be sent in the request body.
   * @param options Optional query parameters and headers.
   * @returns Observable of the API response.
   */
  put<T>(endpoint: string, data: any, options?: HttpOptions): Observable<any> {
    return this.http
      .put<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, data, {
        params: this.processParams(options),
      })
      .pipe(catchError(this.handleError));
  }
  patch<T>(
    endpoint: string,
    data: any,
    options?: HttpOptions
  ): Observable<any> {
    return this.http
      .patch<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, data, {
        params: this.processParams(options),
      })
      .pipe(catchError(this.handleError));
  }
  /**
   * Delete request to remove data at an endpoint.
   * @param endpoint The API endpoint (e.g., 'users/123').
   * @param options Optional query parameters and headers.
   * @returns Observable of the API response.
   */
  delete<T>(
    endpoint: string,
    data?: any,
    options?: HttpOptions
  ): Observable<any> {
    return this.http
      .delete<ApiResponse<T>>(`${this.apiUrl}/${endpoint}`, {
        params: this.processParams(options),
        body: data,
      })
      .pipe(catchError(this.handleError));
  }
  /**
   * Download a file from an endpoint.
   * @param endpoint The API endpoint (e.g., 'files/123').
   * @param options Optional query parameters and headers.
   * @returns Observable of the file as a Blob.
   */
  downloadFile(endpoint: string, options?: HttpOptions): Observable<any> {
    return this.http
      .get(`${this.apiUrl}/${endpoint}`, {
        responseType: 'blob',
        params: this.processParams(options),
      })
      .pipe(catchError(this.handleError));
  }

  /**
   * Upload a file to an endpoint.
   * @param endpoint The API endpoint (e.g., 'files/upload').
   * @param file The file to be uploaded.
   * @param options Optional query parameters and headers.
   * @returns Observable of the API response.
   */
  uploadFile(
    endpoint: string,
    file: File,
    options?: HttpOptions
  ): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http
      .post<ApiResponse<any>>(`${this.apiUrl}/${endpoint}`, formData, {
        params: this.processParams(options),
      })
      .pipe(catchError(this.handleError));
  }
  /**
   * Handle HTTP errors and return a consistent error format.
   * @param error The HTTP error response.
   * @returns Observable of the API error.
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage: string;
    console.log(error, 'error in handleError');

    if (typeof error.message === 'string') {
      try {
        errorMessage =
          JSON.parse(error.message).message || 'An unexpected error occurred';
      } catch (e) {
        errorMessage = error.message;
      }
    } else if (typeof error.error === 'object') {
      errorMessage = JSON.stringify(error.error);
    } else if (typeof error.message === 'object') {
      errorMessage = JSON.stringify(error.message);
    } else if (error.error.message) {
      errorMessage = error.error.message;
    } else {
      errorMessage = 'An unexpected error occurred';
    }

    const apiError: ApiError = {
      code: error.status.toString(),
      message: error.error || errorMessage,
      details: error.error?.details || null,
      timestamp: new Date().toISOString(),
      path: error.url || undefined,
    };

    return throwError(() => apiError);
  }

  /**
   * Example method showing how to use the API with different parameter types
   * This is for demonstration purposes only
   */
  exampleUsage(): void {
    // Example 1: Simple parameters
    this.get('users', {
      params: {
        page: 1,
        pageSize: 10,
        search: 'John',
      },
    }).subscribe((data) => {
      // console.log('Simple params result:', data);
    });

    // Example 2: Array parameters (for multi-select filters)
    this.get('employees', {
      params: {
        departments: [1, 2, 3], // Will be sent as departments=1,2,3
        statuses: ['active', 'pending'],
      },
    }).subscribe((data) => {
      // console.log('Array params result:', data);
    });

    // Example 3: Object parameters
    this.get('reports', {
      params: {
        dateRange: { start: '2023-01-01', end: '2023-12-31' },
        filter: {
          department: 'HR',
          includeInactive: true,
          tags: ['urgent', 'review'],
        },
      },
    }).subscribe((data) => {
      // console.log('Object params result:', data);
    });
  }
}
